import type { Report, SimulationRun, MessageEvent, MessageDefinition } from '@k-ucap/shared';
import { ReportType } from '@k-ucap/shared';

export interface ReportGeneratorInput {
  run: SimulationRun;
  events: MessageEvent[];
  definitions: MessageDefinition[];
  title?: string;
}

export class ReportGeneratorService {
  static generate(input: ReportGeneratorInput): Report {
    const { run, events, definitions, title } = input;
    const definitionMap = new Map(definitions.map((d) => [d.messageType, d]));

    const messageTypeStats: Report['messageTypeStats'] = {};
    const roleErrors: Report['roleErrors'] = [];
    const fieldErrors: Report['fieldErrors'] = [];
    const timingErrors: Report['timingErrors'] = [];
    const alerts: Report['alerts'] = [];
    const vehicleSummary: Report['vehicleSummary'] = {};

    events.forEach((event) => {
      const def = definitionMap.get(event.messageType);
      const validationStatus = event.validationStatus || 'PASS';
      const stats = messageTypeStats[event.messageType] || { count: 0, pass: 0, warn: 0, fail: 0 };
      stats.count += 1;
      if (validationStatus === 'PASS') stats.pass += 1;
      else if (validationStatus === 'WARN') stats.warn += 1;
      else if (validationStatus === 'FAIL') stats.fail += 1;
      messageTypeStats[event.messageType] = stats;

      if (def && event.sourceRole && event.targetRole) {
        const expectedSource = def.sourceRole;
        const expectedTarget = def.targetRole;
        if (
          (expectedSource !== 'ANY' && event.sourceRole !== expectedSource) ||
          (expectedTarget !== 'ANY' && event.targetRole !== expectedTarget)
        ) {
          const existing = roleErrors.find(
            (e) =>
              e.messageType === event.messageType &&
              e.expected === `${expectedSource}->${expectedTarget}` &&
              e.actual === `${event.sourceRole}->${event.targetRole}`
          );
          if (existing) existing.count += 1;
          else
            roleErrors.push({
              messageType: event.messageType,
              expected: `${expectedSource}->${expectedTarget}`,
              actual: `${event.sourceRole}->${event.targetRole}`,
              count: 1,
            });
        }
      }

      if (event.validationResults) {
        const results = event.validationResults as Record<string, { severity: string; message?: string }>;
        Object.entries(results).forEach(([field, result]) => {
          if (result.severity === 'FAIL' || result.severity === 'WARN') {
            const existing = fieldErrors.find((e) => e.messageType === event.messageType && e.field === field);
            if (existing) existing.count += 1;
            else fieldErrors.push({ messageType: event.messageType, field, count: 1 });
          }
        });
      }

      const vehicleId = String(event.fields?.vehicleId ?? event.fields?.uavid ?? 'unknown');
      const coords = event.fields?.coordinates as
        { latitude: number; longitude: number; altitudeM: number } | undefined;
      const lat = coords?.latitude ?? (event.fields?.latitude as number | undefined);
      const lon = coords?.longitude ?? (event.fields?.longitude as number | undefined);
      const alt = coords?.altitudeM ?? (event.fields?.altitudeM as number | undefined);
      const existingVehicle = vehicleSummary[vehicleId] ?? { messageCount: 0 };
      vehicleSummary[vehicleId] = {
        messageCount: existingVehicle.messageCount + 1,
        lastCoordinates:
          lat !== undefined && lon !== undefined
            ? { latitude: lat, longitude: lon, altitudeM: alt ?? 0 }
            : existingVehicle.lastCoordinates,
      };

      if (event.messageType === 1100) {
        alerts.push({
          severity: String(event.fields?.severity ?? 'INFO'),
          message: String(event.fields?.message ?? event.name),
          vehicleId,
          timestamp: event.timestamp,
        });
      }
    });

    const report: Report = {
      id: `report-${Date.now()}`,
      projectId: run.projectId,
      simulationRunId: run.id,
      environment: run.environment,
      type: ReportType.SIMULATION_SUMMARY,
      title: title || `${run.name} - 시험 결과 보고서`,
      summary: {
        totalMessages: run.generatedCount,
        sentCount: run.sentCount,
        droppedCount: run.droppedCount,
        passCount: run.passCount,
        warnCount: run.warnCount,
        failCount: run.failCount,
        durationMs: run.durationMs,
      },
      messageTypeStats,
      roleErrors,
      fieldErrors,
      timingErrors,
      alerts,
      vehicleSummary,
      createdBy: run.createdBy,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return report;
  }
}

export default ReportGeneratorService;
